import type { PostgrestError } from "@supabase/supabase-js";

type SupabaseErrorLike = Partial<Pick<PostgrestError, "code" | "details" | "hint" | "message">> & {
  status?: number | string;
  statusCode?: number | string;
};

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isDuplicateWhatsAppNumberError(error: SupabaseErrorLike): boolean {
  const message = hasText(error.message) ? error.message.toLowerCase() : "";
  const details = hasText(error.details) ? error.details.toLowerCase() : "";

  const mentionsWhatsAppField = message.includes("whatsapp_number") || details.includes("whatsapp_number");
  const isUniqueConflict = error.code === "23505"
    || message.includes("duplicate key value")
    || message.includes("unique constraint")
    || details.includes("already exists");

  return mentionsWhatsAppField && isUniqueConflict;
}

export function getSupabaseErrorMessage(error: unknown, fallback = "Unknown error"): string {
  if (error instanceof Error && hasText(error.message)) {
    return error.message;
  }

  if (!isRecord(error)) {
    return fallback;
  }

  const supabaseError = error as SupabaseErrorLike;

  if (isDuplicateWhatsAppNumberError(supabaseError)) {
    return "This WhatsApp number is already assigned to another account.";
  }

  if (hasText(supabaseError.message) && supabaseError.message !== fallback) {
    return supabaseError.message;
  }

  if (hasText(supabaseError.details)) {
    return supabaseError.details;
  }

  if (hasText(supabaseError.hint)) {
    return supabaseError.hint;
  }

  const status = supabaseError.status ?? supabaseError.statusCode;
  if (status === 409 || status === "409") {
    return "This update conflicts with an existing record.";
  }

  return fallback;
}